// // src/components/page/ar-space/BgParallax.tsx
// 'use client';

// import React, {
//   useEffect,
//   useMemo,
//   useState,
//   useRef,
//   type CSSProperties,
//   type HTMLAttributes,
//   type FC,
// } from 'react';
// import style from './BgParallax.module.scss';

// type BgParallaxProps = HTMLAttributes<HTMLDivElement> & {
//   /** 背景圖片 URL */
//   bgImage: string;
//   /** 由其他元件（例如 CoinCanvas）傳入的左右偏移量 */
//   scrollOffsetX?: number;
//   /** 最終 X 偏移 clamp 的最大值，預設 15 */
//   maxOffset?: number;
//   /** 是否啟用 lazy load（預設 false：一掛上就開始載） */
//   lazy?: boolean;
//   /** lazy 模式下觸發載入的 rootMargin（預設 '0px'） */
//   rootMargin?: string;
//   /** lazy 模式下觸發載入的 threshold（預設 0.01） */
//   threshold?: number;
// };

// // 為了拿到 window.DeviceOrientationEvent 並避免使用 any
// type DeviceOrientationEventWithPermission = {
//   requestPermission?: () => Promise<string>;
// };

// type WindowWithDeviceOrientation = Window & {
//   DeviceOrientationEvent?: DeviceOrientationEventWithPermission;
// };

// const BgParallax: FC<BgParallaxProps> = ({
//   bgImage,
//   scrollOffsetX = 0,
//   maxOffset = 15, // 可調整圖片位移幅度
//   lazy = false,
//   rootMargin = '0px',
//   threshold = 0.01,
//   children,
//   className,
//   style: userStyle,
//   ...rest
// }) => {
//   const containerRef = useRef<HTMLDivElement | null>(null);

//   // 陀螺儀造成的 X 偏移
//   const [gyroOffsetX, setGyroOffsetX] = useState(0);

//   // lazy / 載入狀態
//   const [shouldLoad, setShouldLoad] = useState(!lazy); // 若沒開 lazy，直接載
//   const [loaded, setLoaded] = useState(false);

//   /* ------------------- Lazy load 判斷（是否要開始載入 bgImage） ------------------- */
//   useEffect(() => {
//     // 換了一張 bgImage 時，重置 loaded 狀態
//     setLoaded(false);

//     if (!lazy) {
//       setShouldLoad(true);
//       return;
//     }

//     // lazy 模式下，等 IntersectionObserver 觸發
//     const el = containerRef.current;
//     if (!el) return;

//     if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
//       // 不支援 IO，就直接載
//       setShouldLoad(true);
//       return;
//     }

//     const io = new IntersectionObserver(
//       (entries) => {
//         const first = entries[0];
//         if (first && first.isIntersecting) {
//           setShouldLoad(true);
//           io.disconnect();
//         }
//       },
//       { root: null, rootMargin, threshold }
//     );

//     io.observe(el);
//     return () => {
//       io.disconnect();
//     };
//   }, [lazy, bgImage, rootMargin, threshold]);

//   /* ------------------- 預載背景圖片，完全載入後才顯示 ------------------- */
//   useEffect(() => {
//     if (!shouldLoad || !bgImage) return;
//     if (typeof window === 'undefined') return;

//     let cancelled = false;
//     const img = new window.Image();
//     img.src = bgImage;
//     img.onload = () => {
//       if (!cancelled) {
//         setLoaded(true);
//       }
//     };
//     img.onerror = () => {
//       // 失敗也不要永遠卡在 skeleton，上不上圖就隨緣 XD
//       if (!cancelled) {
//         setLoaded(true);
//       }
//     };

//     return () => {
//       cancelled = true;
//     };
//   }, [shouldLoad, bgImage]);

//   /* ------------------- 陀螺儀偏移 ------------------- */
//   useEffect(() => {
//     if (typeof window === 'undefined') return;

//     const isMobile = window.matchMedia?.('(pointer: coarse)').matches || window.innerWidth <= 768;
//     if (!isMobile) return;

//     const win = window as WindowWithDeviceOrientation;
//     const DeviceOrientationEventGlobal = win.DeviceOrientationEvent;
//     if (!DeviceOrientationEventGlobal) return;

//     const handleOrientation = (event: DeviceOrientationEvent) => {
//       const gamma = event.gamma ?? 0; // 左右傾斜
//       const clamped = Math.max(-15, Math.min(15, gamma));
//       // 映射到 -8 ~ +8 左右，留一點空間給 scrollOffset
//       const offset = -(clamped / 15) * 8;
//       setGyroOffsetX(offset);
//     };

//     let orientationSubscribed = false;
//     const subscribeOrientation = () => {
//       if (orientationSubscribed) return;
//       window.addEventListener('deviceorientation', handleOrientation);
//       orientationSubscribed = true;
//     };

//     let touchListener: ((e: TouchEvent) => void) | null = null;

//     // iOS 13+ 要先 requestPermission
//     if (typeof DeviceOrientationEventGlobal.requestPermission === 'function') {
//       touchListener = () => {
//         DeviceOrientationEventGlobal.requestPermission?.()
//           .then((res) => {
//             if (res === 'granted') {
//               subscribeOrientation();
//             } else {
//               console.warn('[BgParallax] permission not granted:', res);
//             }
//           })
//           .catch((err: unknown) => {
//             console.warn('[BgParallax] permission error:', err);
//           })
//           .finally(() => {
//             if (touchListener) {
//               window.removeEventListener('touchstart', touchListener);
//               touchListener = null;
//             }
//           });
//       };

//       window.addEventListener('touchstart', touchListener, { once: true });
//     } else {
//       // Android / 其他平台
//       subscribeOrientation();
//     }

//     return () => {
//       if (orientationSubscribed) {
//         window.removeEventListener('deviceorientation', handleOrientation);
//       }
//       if (touchListener) {
//         window.removeEventListener('touchstart', touchListener);
//       }
//     };
//   }, []);

//   /* ------------------- 計算背景樣式 ------------------- */
//   const bgStyle: CSSProperties = useMemo(() => {
//     const rawOffset = gyroOffsetX + scrollOffsetX;
//     const clampedOffset = Math.max(-maxOffset, Math.min(maxOffset, rawOffset));

//     return {
//       // 只有在 shouldLoad & loaded & 有 bgImage 時才真的塞入 backgroundImage
//       ...(shouldLoad && loaded && bgImage ? { backgroundImage: `url(${bgImage})` } : {}),

//       backgroundPosition: `${50 + clampedOffset}% center`,
//     };
//   }, [bgImage, gyroOffsetX, scrollOffsetX, maxOffset, shouldLoad, loaded]);

//   // 合併外層傳入的 style（讓外面還是可以加其他 CSS，不會蓋掉背景）
//   const mergedStyle: CSSProperties = useMemo(
//     () => ({
//       ...bgStyle,
//       ...(userStyle as CSSProperties),
//     }),
//     [bgStyle, userStyle]
//   );

//   const rootClassName = [style.bg, !loaded ? style.bgLoading : style.bgLoaded, className ?? '']
//     .filter(Boolean)
//     .join(' ');

//   return (
//     <div ref={containerRef} className={rootClassName} style={mergedStyle} {...rest}>
//       {/* 灰色 / skeleton 覆蓋層（等背景載完會淡出） */}
//       {!loaded && <div className={style.bgSkeleton} />}

//       <div className={style.inner}>{children}</div>
//     </div>
//   );
// };

// export default BgParallax;

'use client';

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type HTMLAttributes,
  type FC,
} from 'react';
import style from './BgParallax.module.scss';

type BgParallaxProps = HTMLAttributes<HTMLDivElement> & {
  bgImage: string;
  scrollOffsetX?: number;
  maxOffset?: number;
  lazy?: boolean;
  rootMargin?: string;
  threshold?: number;
};

// 為了拿到 window.DeviceOrientationEvent 並避免 any
type DeviceOrientationEventWithPermission = {
  requestPermission?: () => Promise<string>;
};

type WindowWithDeviceOrientation = Window & {
  DeviceOrientationEvent?: DeviceOrientationEventWithPermission;
};

const BgParallax: FC<BgParallaxProps> = ({
  bgImage,
  scrollOffsetX = 0,
  maxOffset = 15,
  lazy = false,
  rootMargin = '0px',
  threshold = 0.01,
  children,
  className,
  style: userStyle,
  ...rest
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  // ✅ 不用 state：gyro / scroll 都用 ref + rAF 直接改 DOM（避免 re-render）
  const gyroOffsetXRef = useRef(0);
  const scrollOffsetXRef = useRef(scrollOffsetX);

  const rafRef = useRef<number | null>(null);

  // lazy / 載入狀態
  const [shouldLoad, setShouldLoad] = useState(!lazy);
  const [loaded, setLoaded] = useState(() => !bgImage); // 沒 bgImage 就別顯示 skeleton

  const scheduleApply = () => {
    if (typeof window === 'undefined') return;
    if (rafRef.current != null) return;
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = null;
      const el = containerRef.current;
      if (!el) return;

      const rawOffset = gyroOffsetXRef.current + scrollOffsetXRef.current;
      const clamped = Math.max(-maxOffset, Math.min(maxOffset, rawOffset));

      // ✅ 直接改 DOM，不卡 React render
      el.style.backgroundPosition = `${50 + clamped}% center`;
    });
  };

  // 外部 scrollOffsetX 更新：只更新 ref + rAF
  useEffect(() => {
    scrollOffsetXRef.current = scrollOffsetX;
    scheduleApply();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollOffsetX, maxOffset]);

  /* ------------------- Lazy load 判斷（是否要開始載入 bgImage） ------------------- */
  useEffect(() => {
    // 換 bgImage：重置 loaded（但我們會「立刻掛上 backgroundImage」讓瀏覽器馬上開始抓）
    setLoaded(!bgImage);

    if (!lazy) {
      setShouldLoad(true);
      return;
    }

    const el = containerRef.current;
    if (!el) return;

    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      setShouldLoad(true);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first?.isIntersecting) {
          setShouldLoad(true);
          io.disconnect();
        }
      },
      { root: null, rootMargin, threshold }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [lazy, bgImage, rootMargin, threshold]);

  /* ------------------- 背景圖 decode：只控制 skeleton 淡出，不再阻止背景掛上 ------------------- */
  useEffect(() => {
    if (!shouldLoad || !bgImage) return;
    if (typeof window === 'undefined') return;

    let cancelled = false;
    const img = new window.Image();
    img.src = bgImage;

    const done = () => {
      if (!cancelled) setLoaded(true);
    };

    // decode 比 onload 更穩（可用就用）
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dec = (img as any).decode as undefined | (() => Promise<void>);
    if (typeof dec === 'function') {
      dec.call(img).then(done).catch(done);
    } else {
      img.onload = done;
      img.onerror = done;
    }

    return () => {
      cancelled = true;
    };
  }, [shouldLoad, bgImage]);

  /* ------------------- 陀螺儀偏移：用 ref + rAF（不 setState） ------------------- */
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const isMobile = window.matchMedia?.('(pointer: coarse)').matches || window.innerWidth <= 768;
    if (!isMobile) return;

    const win = window as WindowWithDeviceOrientation;
    const DeviceOrientationEventGlobal = win.DeviceOrientationEvent;
    if (!DeviceOrientationEventGlobal) return;

    const handleOrientation = (event: DeviceOrientationEvent) => {
      const gamma = event.gamma ?? 0;
      const clamped = Math.max(-15, Math.min(15, gamma));
      const offset = -(clamped / 15) * 8; // -8 ~ +8
      gyroOffsetXRef.current = offset;
      scheduleApply();
    };

    let subscribed = false;
    const subscribe = () => {
      if (subscribed) return;
      window.addEventListener('deviceorientation', handleOrientation);
      subscribed = true;
    };

    let touchListener: ((e: TouchEvent) => void) | null = null;

    if (typeof DeviceOrientationEventGlobal.requestPermission === 'function') {
      touchListener = () => {
        DeviceOrientationEventGlobal.requestPermission?.()
          .then((res) => {
            if (res === 'granted') subscribe();
          })
          .finally(() => {
            if (touchListener) {
              window.removeEventListener('touchstart', touchListener);
              touchListener = null;
            }
          });
      };

      window.addEventListener('touchstart', touchListener, { once: true });
    } else {
      subscribe();
    }

    return () => {
      if (subscribed) window.removeEventListener('deviceorientation', handleOrientation);
      if (touchListener) window.removeEventListener('touchstart', touchListener);
    };
  }, [maxOffset]);

  // 初次 mount：先把 position 套上（避免第一次 scroll 才更新）
  useEffect(() => {
    scheduleApply();
    return () => {
      if (rafRef.current != null && typeof window !== 'undefined') {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ------------------- 背景樣式：✅ shouldLoad 就立刻掛 backgroundImage ------------------- */
  const bgStyle: CSSProperties = useMemo(() => {
    return {
      ...(shouldLoad && bgImage ? { backgroundImage: `url(${bgImage})` } : {}),
      // backgroundPosition 由 rAF 直接改 DOM，這裡不用綁 prop，避免 re-render
    };
  }, [shouldLoad, bgImage]);

  const mergedStyle: CSSProperties = useMemo(
    () => ({
      ...bgStyle,
      ...(userStyle as CSSProperties),
    }),
    [bgStyle, userStyle]
  );

  const rootClassName = [style.bg, !loaded ? style.bgLoading : style.bgLoaded, className ?? '']
    .filter(Boolean)
    .join(' ');

  return (
    <div ref={containerRef} className={rootClassName} style={mergedStyle} {...rest}>
      {!loaded && <div className={style.bgSkeleton} />}
      <div className={style.inner}>{children}</div>
    </div>
  );
};

export default BgParallax;
